/* wallGraph.js — derived wall topology (the engine's single connectivity oracle).
 *
 * WHY THIS EXISTS
 * Before this module, four subsystems each re-derived "do these walls meet?" with
 * their own inline tolerance: wallInnerSpan (WT+0.05), _cabWallOverlap (WT+0.05),
 * maybeAutoCorner (0.01) and lhcRoomNormal (0.003). Disagreements between them were
 * the direct cause of the v0.50 corner-bug family (a cabinet refused at an irregular
 * corner that one predicate saw and another didn't). This module provides:
 *   1. ONE canonical joint predicate  — jointsCoincide() / jointTol()
 *   2. A DERIVED topology graph       — buildWallGraph(walls)
 *
 * DERIVED, NOT PERSISTED — deliberately. The engine mutates walls in place while
 * dragging, and the saved design schema must not change (backward compat), so the
 * graph is a pure function of the plain `walls` array, recomputed on demand.
 * Cost: O(n² + E) for n walls — n ≤ a few hundred keeps this well under a
 * millisecond. Callers in per-frame loops should build ONCE per interaction and
 * reuse; the lightweight predicates below stay O(1) for hot paths.
 *
 * WHAT THE GRAPH KNOWS (the "wall intelligence" layer)
 *   nodes  — clustered joints: kind 'free' | 'corner' | 'continuation' | 'tee' | 'cross',
 *            the wall-ends meeting there, raw angle, and for corners on a room the
 *            interior angle + kitchen classification: 'inside' (two runs L-meet)
 *            vs 'outside' (notch / peninsula turn).
 *   walls  — per wall: dir/len, derived segments (T-splits), neighbors at each end,
 *            continuation links, room ownership per side ({left:[roomIds],
 *            right:[roomIds]} — arrays because a T-split wall genuinely borders
 *            several rooms along one side), and interiorNormal when unambiguous.
 *   rooms  — FACES of the planar subdivision (half-edge walk): real room polygons
 *            that stay inside the walls for L / U / staircase shapes — the
 *            principled answer to the old bbox-floor bug (BUG B).
 *
 * Arc walls participate by their CHORD (start→end): consistent with the engine's
 * own placement model ("curve is render-only; placement uses the chord", walls.js).
 * T-junctions are detected for straight walls only (an endpoint of one wall landing
 * mid-segment of another) and split the host into DERIVED segments — the wall model
 * is never mutated.
 *
 * [LOCK-W-08] WALL_THICKNESS is a live mutable binding — always read it at call
 * time (jointTol() is a function, not a constant) so the Wall Properties panel's
 * global thickness edits are honoured everywhere.
 */
import { WALL_THICKNESS } from "./constants";

/** Canonical shared-joint tolerance (v0.50 family: WALL_THICKNESS + 0.05).
 *  Function, not constant — WALL_THICKNESS is a live binding (LOCK-W-08). */
export function jointTol() {
  return WALL_THICKNESS + 0.05;
}

/** THE joint predicate: are two [x,z] points the same wall joint?
 *  [FLIP][W2 knife-edge ruling] '< tol' → '<= tol': at dist === jointTol()
 *  (float64-constructible by imports/tests — commit-snap makes it unreachable
 *  through the UI) the CLUSTER owns the tie. The T-split skip band below
 *  ('t <= tol') is already inclusive on the SKIP side, so at the tie the split
 *  declines and the cluster accepts: exactly one owner, no gap, no
 *  double-claim, and no sub-tol derived sliver segment. */
export function jointsCoincide(a, b, tol = jointTol()) {
  return Math.hypot(a[0] - b[0], a[1] - b[1]) <= tol;
}

/* ── Memoized accessor ─────────────────────────────────────────────────────
 * The engine mutates walls IN PLACE while dragging, so identity-keyed caching
 * would serve stale topology. Instead the memo key is a full content
 * fingerprint (ids + endpoints + type/style + live tol): O(n) to compute per
 * call, rebuild only when geometry actually changed. Single slot — the engine
 * works on one design at a time. arc center/radius are NOT in the key because
 * the graph reads only chord endpoints. */
let _memo = { key: null, graph: null };
export function getWallGraph(walls) {
  const list = walls || [];
  let key = list.length + "|" + jointTol() + "|";
  for (const w of list) {
    if (!w || !Array.isArray(w.start) || !Array.isArray(w.end)) continue;
    key += w.id + ":" + w.start[0] + "," + w.start[1] + ";" + w.end[0] + "," + w.end[1] + ";" + (w.type || "") + (w.style || "") + "|";
  }
  if (_memo.key === key) return _memo.graph;
  const graph = buildWallGraph(list);
  _memo = { key, graph };
  return graph;
}

const COLLINEAR_DEG = 2;          // ≤2° off straight ⇒ 'continuation', not 'corner'
const MIN_ROOM_AREA_M2 = 0.05;    // faces smaller than this are spur artefacts, not rooms
const MIN_WALL_LEN = 0.01;        // engine-wide degenerate-wall threshold
const PROBE_EPS = 0.02;           // 2 cm side-probe — inside any real room, off any boundary

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

/** Ray-cast point-in-polygon ([x,z] pairs). Probes are always nudged off the
 *  boundary by PROBE_EPS, so edge-exact behavior never matters here. */
export function pointInPolygon(p, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, zi] = poly[i], [xj, zj] = poly[j];
    if ((zi > p[1]) !== (zj > p[1]) &&
        p[0] < ((xj - xi) * (p[1] - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

function signedArea(poly) {
  let s = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++)
    s += (poly[j][0] * poly[i][1]) - (poly[i][0] * poly[j][1]);
  return s / 2;
}

/** buildWallGraph(walls, {tol}) → { tol, nodes, walls: Map<wallId,info>, rooms }.
 *  Pure; never throws on degenerate input (unusable walls are skipped). */
export function buildWallGraph(walls, opts = {}) {
  const tol = opts.tol != null ? opts.tol : jointTol();
  const usable = (walls || []).filter(
    (w) => w && Array.isArray(w.start) && Array.isArray(w.end) && dist(w.start, w.end) >= MIN_WALL_LEN
  );

  /* ── 1. Cluster wall endpoints into joint nodes (union-find over pairwise tol) ── */
  const ends = [];
  usable.forEach((w) => {
    ends.push({ wallId: w.id, end: "start", p: w.start });
    ends.push({ wallId: w.id, end: "end", p: w.end });
  });
  const parent = ends.map((_, i) => i);
  const find = (i) => { while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; } return i; };
  const union = (i, j) => { const a = find(i), b = find(j); if (a !== b) parent[b] = a; };
  for (let i = 0; i < ends.length; i++)
    for (let j = i + 1; j < ends.length; j++)
      if (jointsCoincide(ends[i].p, ends[j].p, tol)) union(i, j);

  const nodeByRoot = new Map();
  const nodes = [];
  const nodeOfEnd = new Map(); // "wallId:start|end" → node
  ends.forEach((e, i) => {
    const r = find(i);
    let n = nodeByRoot.get(r);
    if (!n) { n = { id: "n" + nodes.length, p: [0, 0], ends: [], _pts: [] }; nodeByRoot.set(r, n); nodes.push(n); }
    n.ends.push({ wallId: e.wallId, end: e.end });
    n._pts.push(e.p);
    nodeOfEnd.set(e.wallId + ":" + e.end, n);
  });
  const centroidOf = (n) => { // node position = centroid of its member endpoints
    n.p = [n._pts.reduce((s, p) => s + p[0], 0) / n._pts.length, n._pts.reduce((s, p) => s + p[1], 0) / n._pts.length];
  };
  nodes.forEach(centroidOf);

  /* ── 1b. FACE-VERTEX CAPTURE (2026-09-15 immutable-authoring contract) ──
     Legs are authored as their reference offset by t/2 and NEVER re-mitred, so
     a leg drawn to a corner's inside vertex has its centerline endpoint h off
     that vertex — up to h·(1/sin(θ/2) + 1) from the centerline NODE, i.e.
     beyond `tol` at inside corners sharper than ~70° — while its FACE ends
     exactly on the vertex. Joint ownership is therefore decided by the joint's
     FACE geometry as well: a dangling endpoint whose own face line passes
     through a 2-end corner's mitre vertex — i.e. within its own half-thickness
     (+2 cm slack) of that vertex — joins that node. Corner mitre vertices ONLY
     (free-end cap corners are not sources: a stub ending near another wall's
     cap must keep the W1-gate T/L and crossing-ownership rulings), and the
     tight radius means nothing but a leg drawn INTO the corner is captured.
     Derived and cheap (one line intersection per corner side); a strict no-op
     for every endpoint the plain distance rule already clustered. Arcs excluded. ── */
  const wallById = new Map(usable.map((w) => [w.id, w]));
  const halfT = (w) => (typeof w.thickness === "number" && isFinite(w.thickness) && w.thickness > 0 ? w.thickness : WALL_THICKNESS) / 2;
  const endPt = (e) => { const w = wallById.get(e.wallId); return e.end === "start" ? w.start : w.end; };
  const outward = (e) => { // unit direction from the endpoint INTO the wall body
    const w = wallById.get(e.wallId);
    const a = e.end === "start" ? w.start : w.end, b = e.end === "start" ? w.end : w.start;
    const L = dist(a, b) || 1;
    return [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
  };
  const faceVertices = (n) => {
    if (n.ends.some((e) => wallById.get(e.wallId).type === "arc")) return [];
    if (n.ends.length === 2) {                    // L corner: its two mitre vertices (own-axis face lines)
      const [ea, eb] = n.ends;
      const pa = endPt(ea), pb = endPt(eb), da = outward(ea), db = outward(eb);
      const ha = halfT(wallById.get(ea.wallId)), hb = halfT(wallById.get(eb.wallId));
      const den = da[0] * db[1] - da[1] * db[0];
      if (Math.abs(den) < 1e-9) return [];        // continuation / reversal: no mitre vertex
      const out = [];
      for (const s of [1, -1]) {                   // a's ±face ∩ b's ∓face
        const qa = [pa[0] - da[1] * ha * s, pa[1] + da[0] * ha * s];
        const qb = [pb[0] + db[1] * hb * s, pb[1] - db[0] * hb * s];
        const t = ((qb[0] - qa[0]) * db[1] - (qb[1] - qa[1]) * db[0]) / den;
        const v = [qa[0] + da[0] * t, qa[1] + da[1] * t];
        if (isFinite(v[0]) && isFinite(v[1]) && dist(v, n.p) <= 4 * Math.max(ha, hb) + tol) out.push(v); // miter-limit discipline: no spikes
      }
      return out;
    }
    return [];
  };
  const captureSources = nodes.filter((n) => n.ends.length === 2).map((n) => ({ n, vs: faceVertices(n) })).filter((c) => c.vs.length);
  for (const free of nodes.slice()) {
    if (free.ends.length !== 1) continue;
    const fe = free.ends[0];
    const fw = wallById.get(fe.wallId);
    if (!fw || fw.type === "arc") continue;
    let best = null, bestD = halfT(fw) + 0.02;                                 // its face runs through the vertex
    for (const c of captureSources) {
      if (c.n === free || c.n.ends.length !== 2) continue;                     // merged already → stale vertices
      if (c.n.ends.some((e) => e.wallId === fe.wallId)) continue;            // never a wall's own other end
      for (const v of c.vs) { const d = dist(free.p, v); if (d <= bestD) { bestD = d; best = c.n; } }
    }
    if (!best) continue;
    best.ends.push(fe);
    best._pts.push(endPt(fe));
    centroidOf(best);
    nodeOfEnd.set(fe.wallId + ":" + fe.end, best);
    nodes.splice(nodes.indexOf(free), 1);
  }
  nodes.forEach((n, i) => { n.id = "n" + i; delete n._pts; });

  /* ── 2. T-junctions: a node landing mid-segment of a STRAIGHT wall splits it
        (derived split only — the wall model is untouched). Arc chords are not
        T-split: a chord projection would misplace the joint on the curve. ── */
  const splitsByWall = new Map(); // wallId → [{t, node}] sorted by t
  usable.forEach((w) => {
    if (w.type === "arc") return;
    const L = dist(w.start, w.end);
    const ux = (w.end[0] - w.start[0]) / L, uz = (w.end[1] - w.start[1]) / L;
    nodes.forEach((n) => {
      if (n.ends.some((e) => e.wallId === w.id)) return; // already incident at an end
      const t = (n.p[0] - w.start[0]) * ux + (n.p[1] - w.start[1]) * uz;
      if (t <= tol || t >= L - tol) return;              // near-end hits already clustered
      const px = w.start[0] + ux * t, pz = w.start[1] + uz * t;
      if (dist([px, pz], n.p) < tol) {
        if (!splitsByWall.has(w.id)) splitsByWall.set(w.id, []);
        splitsByWall.get(w.id).push({ t, node: n });
      }
    });
    if (splitsByWall.has(w.id)) splitsByWall.get(w.id).sort((a, b) => a.t - b.t);
  });

  /* ── 3. Derived segments (walls split at T-nodes) + half-edges ── */
  const segments = []; // {wallId, n0, n1}
  usable.forEach((w) => {
    const chain = [nodeOfEnd.get(w.id + ":start"), ...(splitsByWall.get(w.id) || []).map((s) => s.node), nodeOfEnd.get(w.id + ":end")];
    for (let i = 0; i < chain.length - 1; i++)
      if (chain[i] !== chain[i + 1]) segments.push({ wallId: w.id, n0: chain[i], n1: chain[i + 1] });
  });
  const segsAt = new Map(); // node.id → outgoing half-edges
  const halfEdges = [];
  segments.forEach((seg) => {
    const d = [seg.n1.p[0] - seg.n0.p[0], seg.n1.p[1] - seg.n0.p[1]];
    const L = Math.hypot(d[0], d[1]) || 1;
    const he1 = { from: seg.n0, to: seg.n1, wallId: seg.wallId, seg, dir: [d[0] / L, d[1] / L] };
    const he2 = { from: seg.n1, to: seg.n0, wallId: seg.wallId, seg, dir: [-d[0] / L, -d[1] / L] };
    he1.twin = he2; he2.twin = he1;
    halfEdges.push(he1, he2);
    if (!segsAt.has(seg.n0.id)) segsAt.set(seg.n0.id, []);
    if (!segsAt.has(seg.n1.id)) segsAt.set(seg.n1.id, []);
    segsAt.get(seg.n0.id).push(he1);
    segsAt.get(seg.n1.id).push(he2);
  });

  /* ── 4. Node classification (by incident segment degree) ── */
  nodes.forEach((n) => {
    const out = segsAt.get(n.id) || [];
    const deg = out.length;
    if (deg <= 1) n.kind = "free";
    else if (deg === 2) {
      const a = out[0].dir, b = out[1].dir;
      const ang = Math.acos(Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1]))) * 180 / Math.PI;
      n.angleDeg = ang; // raw angle between the two wall directions (0..180)
      n.kind = Math.abs(180 - ang) <= COLLINEAR_DEG ? "continuation" : "corner";
    } else if (deg === 3) n.kind = "tee";
    else n.kind = "cross";
    n.wallIds = [...new Set(out.map((h) => h.wallId))];
  });

  /* ── 5. Faces via half-edge walk. next(he) = rotational successor of twin at the
        head node (outgoing edges CCW-sorted by angle). Every face is then tested
        for BOUNDEDNESS convention-free: probe a point on the traversal-hand side
        of a non-spur edge (left of travel when the loop's signed area is positive,
        right when negative — a simple closed curve walked CCW keeps its interior
        on the left); the probe lands inside the face's own polygon iff the face is
        the bounded region. The unbounded outer face always fails this probe. ── */
  nodes.forEach((n) => (segsAt.get(n.id) || []).sort((a, b) => Math.atan2(a.dir[1], a.dir[0]) - Math.atan2(b.dir[1], b.dir[0])));
  const nextHE = (he) => {
    // Rotational PREDECESSOR of the twin in CCW order = the sharpest clockwise
    // turn from the incoming direction — the standard traversal that keeps each
    // face's region on the LEFT and interior faces CCW. (Successor-of-twin looks
    // identical at degree-2 nodes but walks AROUND partitions at tees.)
    const out = segsAt.get(he.to.id);
    const idx = out.indexOf(he.twin);
    return out[(idx - 1 + out.length) % out.length];
  };
  const faces = [];
  const seen = new Set();
  halfEdges.forEach((start) => {
    if (seen.has(start)) return;
    const loopHEs = [];
    let he = start, guard = halfEdges.length + 1;
    do { seen.add(he); loopHEs.push(he); he = nextHE(he); } while (he !== start && --guard > 0);
    if (guard <= 0) return; // malformed — never emit a partial face
    const poly = loopHEs.map((h) => h.from.p);
    faces.push({ hes: loopHEs, poly, area: signedArea(poly) });
  });
  const faceIsBounded = (f) => {
    if (Math.abs(f.area) < MIN_ROOM_AREA_M2) return false;          // spur-only artefact
    const edge = f.hes.find((h) => !f.hes.includes(h.twin)) || f.hes[0]; // non-spur edge if any
    const midX = (edge.from.p[0] + edge.to.p[0]) / 2, midZ = (edge.from.p[1] + edge.to.p[1]) / 2;
    // Under nextHE (outgoing edges CCW-sorted, successor-of-twin) every face keeps
    // its region on the LEFT of travel — a fixed hand, NOT a function of area sign.
    // A bounded face therefore contains a left-side probe of its own boundary; the
    // unbounded outer face (same polygon, reversed walk) does not.
    const q = [midX - edge.dir[1] * PROBE_EPS, midZ + edge.dir[0] * PROBE_EPS];
    return pointInPolygon(q, f.poly);
  };
  const rooms = faces.filter(faceIsBounded).map((f, i) => ({
    id: "r" + i,
    polygon: f.poly.map((p) => [p[0], p[1]]),
    nodeIds: f.hes.map((h) => h.from.id),
    wallIds: [...new Set(f.hes.map((h) => h.wallId))],
    areaM2: Math.abs(f.area),
  }));

  /* ── 6. Per-wall info: direction, neighbors, per-segment room sides, normal.
        Side probes use SEGMENT midpoints (clear of junction nodes by ≥ tol). A wall
        may border several rooms along one side (T-split bar) — sides are arrays.
        interiorNormal is set only when rooms exist on exactly ONE side. ── */
  const wallInfo = new Map();
  usable.forEach((w) => {
    const L = dist(w.start, w.end);
    const dir = [(w.end[0] - w.start[0]) / L, (w.end[1] - w.start[1]) / L];
    const left = [-dir[1], dir[0]]; // left-perp of start→end (matches getWallPlacementInfo's first guess)
    const n0 = nodeOfEnd.get(w.id + ":start"), n1 = nodeOfEnd.get(w.id + ":end");
    const neighborsOf = (node) => node.ends.filter((e) => e.wallId !== w.id).map((e) => ({ wallId: e.wallId, end: e.end }));
    const roomAt = (q) => { const r = rooms.find((rm) => pointInPolygon(q, rm.polygon)); return r ? r.id : null; };
    const segInfos = segments.filter((s) => s.wallId === w.id).map((s) => {
      const mx = (s.n0.p[0] + s.n1.p[0]) / 2, mz = (s.n0.p[1] + s.n1.p[1]) / 2;
      return {
        n0: s.n0.id, n1: s.n1.id,
        left: roomAt([mx + left[0] * PROBE_EPS, mz + left[1] * PROBE_EPS]),
        right: roomAt([mx - left[0] * PROBE_EPS, mz - left[1] * PROBE_EPS]),
      };
    });
    const leftRooms = [...new Set(segInfos.map((s) => s.left).filter(Boolean))];
    const rightRooms = [...new Set(segInfos.map((s) => s.right).filter(Boolean))];
    let interiorNormal = null;
    if (leftRooms.length && !rightRooms.length) interiorNormal = left;
    else if (rightRooms.length && !leftRooms.length) interiorNormal = [-left[0], -left[1]];
    const contAt = (node) =>
      node.kind === "continuation" ? (node.ends.find((e) => e.wallId !== w.id) || {}).wallId || null : null;
    wallInfo.set(w.id, {
      id: w.id, len: L, dir, startNode: n0.id, endNode: n1.id,
      neighborsAtStart: neighborsOf(n0), neighborsAtEnd: neighborsOf(n1),
      continuationAtStart: contAt(n0), continuationAtEnd: contAt(n1),
      segments: segInfos,
      roomSides: { left: leftRooms, right: rightRooms },
      interiorNormal,
      splits: (splitsByWall.get(w.id) || []).map((s) => ({ t: s.t, nodeId: s.node.id })),
    });
  });

  /* ── 7. Corner classification (kitchen sense) for corner nodes on a room:
        probe the angle bisector — inside the room ⇒ interior angle < 180° ⇒
        'inside' corner (two runs L-meet); outside ⇒ reflex ⇒ 'outside' corner. ── */
  nodes.forEach((n) => {
    if (n.kind !== "corner") { n.cornerKind = null; return; }
    const room = rooms.find((r) => r.nodeIds.includes(n.id));
    if (!room) { n.cornerKind = null; return; }
    const out = segsAt.get(n.id);
    const bis = [out[0].dir[0] + out[1].dir[0], out[0].dir[1] + out[1].dir[1]];
    const bl = Math.hypot(bis[0], bis[1]);
    if (bl < 1e-9) { n.cornerKind = null; n.interiorAngleDeg = 180; return; } // straight — unreachable for 'corner'
    const q = [n.p[0] + (bis[0] / bl) * PROBE_EPS, n.p[1] + (bis[1] / bl) * PROBE_EPS];
    const bisectorInside = pointInPolygon(q, room.polygon);
    n.interiorAngleDeg = bisectorInside ? n.angleDeg : 360 - n.angleDeg;
    n.cornerKind = bisectorInside ? "inside" : "outside";
    n.roomId = room.id;
  });

  return { tol, nodes, walls: wallInfo, rooms };
}
